// ABOUTME: Authentication context providing global auth state management with AWS Amplify
// ABOUTME: Manages user authentication with Cognito Hosted UI, admin detection, and sign in/out functionality

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { getCurrentUser, signOut, signInWithRedirect, fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

interface User {
  username: string;
  email: string;
  name: string;
  picture?: string;
  groups: string[];
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAdmin: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAdmin = user?.groups?.includes('Admins') || false;

  useEffect(() => {
    checkAuthState();
    
    // Listen for auth events from Amplify Hub
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      console.log('Hub auth event:', payload);
      if (payload.event === 'signedIn') {
        checkAuthState();
      } else if (payload.event === 'signedOut') {
        setUser(null);
      }
    });
    
    // Re-check auth state when window gains focus (after OAuth redirect)
    const handleFocus = () => checkAuthState();
    window.addEventListener('focus', handleFocus);
    
    return () => {
      unsubscribe();
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const checkAuthState = async () => {
    try {
      console.log('Checking auth state...');
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();

      // Extract groups from JWT token
      const groups = (session.tokens?.idToken?.payload['cognito:groups'] as string[]) || [];

      console.log('Current user:', currentUser);
      console.log('User groups:', groups);

      const userData: User = {
        username: currentUser.username,
        email: currentUser.signInDetails?.loginId || '',
        name: currentUser.signInDetails?.loginId || currentUser.username,
        groups: groups
      };
      setUser(userData);
      console.log('User set:', userData);
    } catch (error) {
      console.log('Auth check failed:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const signIn = async () => {
    try {
      await signInWithRedirect();
    } catch (error) {
      console.error('Sign in error:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setUser(null);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAdmin,
    signIn,
    signOut: handleSignOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
